import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import get from 'lodash/get';
import { useTranslation } from 'react-i18next';
import OnScrollToElement from '@wix/da-shared-react/pkg/OnScrollToElement';
import ErrorBoundary from '@wix/da-shared-react/pkg/ErrorBoundary';
import LoadingIndicator from '@wix/da-shared-react/pkg/LoadingIndicator';
import { useIsMounted } from '@wix/da-shared-react/pkg/utils/hooks/useIsMounted';
import withSectionFetchSpinner from '../../../decorators/withSectionFetchSpinner';
import WideWidgetLayout from '../../Layout/WideWidgetLayout';
import SideNavBar from '../../SideNavBar';
import TimePeriodSwitcherSticky from './TimePeriodSwitcherSticky';
import EmptyState from '../../EmptyState';

// Lazy loaded widgets
const OverviewWidget = lazy(() =>
  import(/* webpackChunkName: "stats" */ './OverviewWidget')
);
const MySubmissionsWidget = lazy(() =>
  import(/* webpackChunkName: "stats" */ './MySubmissionsWidget')
);
const GeneralWidget = lazy(() =>
  import(/* webpackChunkName: "stats" */ './GeneralWidget')
);

import profilesCssVars from '../../../styles/variables.scss';
const scrollToChildOffsetTop = parseInt(
  profilesCssVars['navbar-sticky-height']
);

import s from './StatsSection.scss';

export interface Props {
  hasCalculatedStats: boolean;
}

export const StatsSection: React.FC<Props> = ({ hasCalculatedStats }) => {
  const [activeHash, setActiveHash] = useState('overview');
  const isMounted = useIsMounted();
  const widgetRefs = useRef<(HTMLDivElement)[]>([]);
  const pushWidgetRef = ref => {
    widgetRefs.current.push(ref);
  };

  useEffect(() => {
    const initHash = get(document, 'location.hash').slice(1);
    if (initHash) {
      setActiveHash(initHash);
    }
  }, []);

  const updateActiveHash = (hash: string) => {
    setActiveHash(hash);
    history.replaceState({}, '', `#${hash}`);
  };

  const onScolledToElement = (element: HTMLElement) => {
    updateActiveHash(element.id);
  };

  const { t } = useTranslation();

  if (!isMounted) {
    return null;
  }

  if (!hasCalculatedStats) {
    return (
      <EmptyState
        title={t('pages.stats.empty.title')}
        subtitle={t('pages.stats.empty.subtitle')}
        className={s['empty']}
      />
    );
  }

  return (
    <WideWidgetLayout
      header={t('pages.stats.title')}
      subSubheader={t('pages.stats.subtitle')}
      headerExtras={<TimePeriodSwitcherSticky />}
      navigation={
        <SideNavBar
          routes={[
            {
              hash: 'overview',
              transKey: 'pages.stats.widgets.overview.title',
            },
            {
              hash: 'my_submissions',
              transKey: 'pages.stats.widgets.my_submissions.title',
            },
            { hash: 'traffic', transKey: 'pages.stats.widgets.traffic.title' },
            { hash: 'general', transKey: 'pages.stats.widgets.general.title' },
          ]}
          activeHash={activeHash}
          onActiveHashChange={updateActiveHash}
        />
      }
    >
      <Suspense fallback={LoadingIndicator}>
        <OverviewWidget id="overview" ref={pushWidgetRef} />
        <MySubmissionsWidget id="my_submissions" ref={pushWidgetRef} />
        <GeneralWidget id="general" ref={pushWidgetRef} />
      </Suspense>
      <ErrorBoundary hideOnFail>
        <OnScrollToElement
          elements={widgetRefs.current}
          offsetTop={scrollToChildOffsetTop}
          onScrollTo={onScolledToElement}
        />
      </ErrorBoundary>
    </WideWidgetLayout>
  );
};
StatsSection.displayName = 'StatsSection';

export default withSectionFetchSpinner(StatsSection);
